import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";
import { formatQuantityValue } from "../../utils/formatQuantityValue";
import { QuantityDelta } from "../QuantityDelta";
import { KIND_LABEL_KEY, TYPE_LABEL_KEY } from "../constants";
import type {
  QuantityDiffSet,
  QuantityDiffStatus,
} from "../../utils/computeQuantityDiff";
import type { QuantityKind } from "../../utils/formatQuantityValue";
import type { QuantityDiffSummaryProps } from "./types";

interface SummaryCardProps {
  label: string;
  diffSet: QuantityDiffSet;
  kind: QuantityKind;
  isActive: boolean;
  onSelect: () => void;
}

const BREAKDOWN_STATUSES: QuantityDiffStatus[] = [
  "added",
  "removed",
  "changed",
];

const SummaryCard = ({
  label,
  diffSet,
  kind,
  isActive,
  onSelect,
}: SummaryCardProps) => {
  const { t, i18n } = useTranslation();

  const formatValue = (value: number) =>
    formatQuantityValue({ value, language: i18n.language, kind });

  const breakdownSegments = useMemo(
    () =>
      BREAKDOWN_STATUSES.map((status) => ({
        status,
        count: diffSet.entries.filter((entry) => entry.status === status)
          .length,
      }))
        .filter((segment) => segment.count > 0)
        .map((segment) =>
          t(`smartSurveyor.planComparison.quantities.${segment.status}Count`, {
            count: segment.count,
          }),
        ),
    [diffSet, t],
  );

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={isActive}
      className={clsx(
        "flex-1 rounded-xl border bg-white px-4 py-3 text-left transition-colors",
        isActive
          ? "border-blue-200 ring-1 ring-blue-100"
          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50",
      )}
    >
      <span className="flex items-center justify-between gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
          {label}
        </span>
        <QuantityDelta delta={diffSet.totals.delta} kind={kind} />
      </span>
      <span className="mt-2 flex items-baseline gap-3 sm:gap-4">
        <span className="block min-w-0">
          <span className="block whitespace-nowrap text-lg font-semibold tabular-nums text-gray-900 sm:text-xl">
            {formatValue(diffSet.totals.beforeQuantity)}
          </span>
          <span className="block text-xs text-gray-500">
            {t("smartSurveyor.planComparison.quantities.before")}
          </span>
        </span>
        <span className="text-gray-400">→</span>
        <span className="block min-w-0">
          <span className="block whitespace-nowrap text-lg font-semibold tabular-nums text-gray-900 sm:text-xl">
            {formatValue(diffSet.totals.afterQuantity)}
          </span>
          <span className="block text-xs text-gray-500">
            {t("smartSurveyor.planComparison.quantities.after")}
          </span>
        </span>
      </span>
      {/* Totals alone can net out to zero while many types changed. */}
      <span className="mt-2 block text-xs text-gray-600">
        {`${t(TYPE_LABEL_KEY[kind])}: `}
        {breakdownSegments.length === 0
          ? t("smartSurveyor.planComparison.quantities.noDifferences")
          : breakdownSegments.join(" · ")}
      </span>
    </button>
  );
};

export const QuantityDiffSummary = ({
  elements,
  cables,
  activeKind,
  onKindSelect,
}: QuantityDiffSummaryProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-3 sm:flex-row">
      <SummaryCard
        label={t(KIND_LABEL_KEY.elements)}
        diffSet={elements}
        kind="elements"
        isActive={activeKind === "elements"}
        onSelect={() => onKindSelect("elements")}
      />
      <SummaryCard
        label={t(KIND_LABEL_KEY.cables)}
        diffSet={cables}
        kind="cables"
        isActive={activeKind === "cables"}
        onSelect={() => onKindSelect("cables")}
      />
    </div>
  );
};
